import React from 'react';

import {ConnectorSizing, SharedDomainParams} from 'antibodies/AntibodyTypedefs';
import AntibodyComponentPopover from 'antibodies/visualization/AntibodyComponentPopover';
import {ArcPathParameterNode, LinePathParameterNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {assertNever} from 'util/Types';

export enum PathParameterType {
  LINE = 'LINE',
  ARC = 'ARC',
}

export enum ArcDirection {
  CLOCKWISE = 'CLOCKWISE',
  COUNTER_CLOCKWISE = 'COUNTER_CLOCKWISE',
}

export enum ArcSize {
  LARGE = 'LARGE',
  SMALL = 'SMALL',
}

// A straight line segment that is dx and dy distance away from the current position of the path,
// following the SVG coordinate grid (positive dx is to the right, positive dy is down).
interface LinePathParameter extends Omit<LinePathParameterNode, '__typename'> {
  type: PathParameterType.LINE;
}

// An arc segment that ends at dx and dy distance away from the current position of the path,
// following the SVG coordinate grid (positive dx is to the right, positive dy is down).
// rx and ry are the horizontal and vertical radii of the ellipse.
interface ArcPathParameter extends Omit<ArcPathParameterNode, '__typename'> {
  type: PathParameterType.ARC;
}

export type PathParameter = LinePathParameter | ArcPathParameter;

type AntibodyPathLinkerProps = SharedDomainParams & {
  pathParameters: Array<PathParameter>;
  color: string;
  connectorSizing: ConnectorSizing;
};

/**
 * Renders a linker that can be composed of any line path, as described by pathParameters,
 * which a list of ordered straight lines and arcs that define the path drawing the linker, each relative
 * to the previous part of the path. The path starts at (0, 0) and follows the SVG coordinate grid.
 */
export default function AntibodyPathLinker({
  pathParameters,
  color,
  proteinId,
  inaccessible,
  label,
  showPopovers,
  connectorSizing,
  antibodyId,
}: AntibodyPathLinkerProps): JSX.Element {
  const {strokeThickness} = connectorSizing;

  function getPath(): string {
    let path = 'M 0 0';
    for (const parameter of pathParameters) {
      switch (parameter.type) {
        case PathParameterType.LINE:
          path += ` l ${parameter.dx} ${parameter.dy}`;
          break;
        case PathParameterType.ARC: {
          const sweep = parameter.direction === ArcDirection.CLOCKWISE ? 1 : 0;
          const largeArc = parameter.arcSize === ArcSize.LARGE ? 1 : 0;
          path += ` a ${parameter.rx} ${parameter.ry} ${parameter.rotation} ${largeArc} ${sweep} ${parameter.dx} ${parameter.dy}`;
          break;
        }
        default:
          assertNever(parameter);
          break;
      }
    }
    return path;
  }

  return (
    <AntibodyComponentPopover
      antibodyId={antibodyId}
      proteinId={proteinId}
      inaccessible={inaccessible}
      componentLabel={label}
      showPopovers={showPopovers}
    >
      <path d={getPath()} stroke={color} strokeWidth={strokeThickness} fill="none" />
    </AntibodyComponentPopover>
  );
}
